import React, { useState, useEffect } from 'react';
import { mockArticles } from '../data/mockResearch'; // Keeping for fallback if needed, but will use API
import ArticleCard from '../components/research/ArticleCard';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import axios from 'axios';

const ResearchHubPage = () => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const categories = ['All', 'Stock', 'Industry', 'Macro', 'AI Analysis', 'Human Written'];

    const API_BASE = 'http://localhost:8000/api/research';

    useEffect(() => {
        const fetchArticles = async () => {
            setLoading(true);
            try {
                let url = `${API_BASE}/articles/?status=published`;

                // Map frontend display tabs to API filters
                if (activeCategory === 'Stock') url += '&category=stock';
                if (activeCategory === 'Industry') url += '&category=industry';
                if (activeCategory === 'Macro') url += '&category=macro';
                if (activeCategory === 'AI Analysis') url += '&author_type=ai';
                if (activeCategory === 'Human Written') url += '&author_type=human';

                const response = await axios.get(url);
                setArticles(response.data);
            } catch (error) {
                console.error("Error fetching research articles:", error);
                // Fallback to empty if API fails for now, or keep mock if you prefer
                setArticles([]);
            } finally {
                setLoading(false);
            }
        };

        fetchArticles();
    }, [activeCategory]);

    const featuredArticle = articles.find(a => a.is_featured) || articles[0];
    const regularArticles = articles.filter(a => a.id !== featuredArticle?.id);

    return (
        <div className="flex flex-col min-h-screen bg-[#F9FAFB]">
            <Navigation />
            <main className="flex-1 pt-24 pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Header Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-5xl md:text-7xl font-black text-[#222425] mb-6 tracking-tight">
                            Research <span className="text-[#00DA76]">&</span> Insights
                        </h1>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto font-medium">
                            Deep dives on stocks, sectors & macro by human and AI analysts.
                        </p>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-6 py-2.5 rounded-full text-xs font-black transition-all duration-300 border ${activeCategory === cat
                                    ? 'bg-[#222425] text-white border-[#222425] shadow-lg scale-105'
                                    : 'bg-white text-gray-400 border-gray-100 hover:border-gray-300 hover:text-gray-600'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {/* Hero Section */}
                    <div className="mb-16">
                        {loading ? (
                            <div className="h-96 bg-white rounded-3xl animate-pulse border border-gray-100" />
                        ) : featuredArticle ? (
                            <ArticleCard article={featuredArticle} isFeatured={true} />
                        ) : (
                            <div className="h-64 bg-white rounded-3xl border border-dashed border-gray-200 flex items-center justify-center text-gray-400 italic">
                                No featured articles in this category.
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                        {/* Main Content Grid */}
                        <div className="lg:col-span-3">
                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {[1, 2, 3, 4].map(n => (
                                        <div key={n} className="h-64 bg-white rounded-3xl animate-pulse border border-gray-100" />
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {regularArticles.map((article) => (
                                        <ArticleCard key={article.id} article={article} />
                                    ))}

                                    {regularArticles.length === 0 && !featuredArticle && (
                                        <div className="col-span-full py-20 text-center">
                                            <p className="text-gray-400 text-lg">No articles found in this category.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-1 space-y-10">
                            {/* Latest Reports Sidebar Widget */}
                            <div className="bg-[#222425] rounded-3xl p-6 shadow-xl text-white">
                                <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-gray-400">Latest Reports</h3>
                                <div className="space-y-6">
                                    <div className="group cursor-pointer">
                                        <div className="text-[10px] font-black text-[#00DA76] uppercase mb-1">Weekly Market Wrap</div>
                                        <div className="text-sm font-bold group-hover:text-[#00DA76] transition-colors">Dhaka Stock Exchange: Weekly Performance Overview</div>
                                    </div>
                                    <div className="group cursor-pointer">
                                        <div className="text-[10px] font-black text-[#00DA76] uppercase mb-1">Sector Outlook</div>
                                        <div className="text-sm font-bold group-hover:text-[#00DA76] transition-colors">Cement & Materials: Q4 Demand Projections</div>
                                    </div>
                                </div>
                                <button className="w-full mt-8 py-3 bg-[#333] hover:bg-[#444] rounded-xl text-xs font-bold transition-all border border-[#444]">
                                    View All Reports
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ResearchHubPage;
